/**
 * Typed client for the Learn Worker.
 *
 * Everything goes through `request`, which unwraps the `{ error: { code,
 * message }, requestId }` envelope and throws an `ApiError` carrying the
 * machine-readable code — the same contract website-main, website-cloud,
 * website-enterprise, Review and Scope use, so a failure reads the same
 * wherever it happens.
 *
 * ── One departure from the suite, on purpose ────────────────────────────────
 * Every other repo re-declares its wire types in this file, so a handler's
 * response cannot change under a screen without somebody editing both. That is
 * right when one person writes the two in sequence. Here the contract
 * (`worker/wire.ts`) was written first and the Worker and this client are
 * built against it at the same time, so a second copy would be a second
 * contract by the end of the day. The import is **type-only** and the `@wire`
 * alias resolves to `worker/wire.ts`: esbuild strips it before resolution, so
 * nothing from `worker/` reaches the browser bundle. A value import from
 * `@wire` would, and is the one thing not to add here.
 *
 * ── Two things this client relies on and does not do ────────────────────────
 *   · **`Origin` on mutations.** The Worker refuses a cookie-credentialed
 *     POST/PUT/PATCH/DELETE whose `Origin` is not Learn's own (403
 *     `bad_origin`). Browsers attach that header to every non-GET fetch on
 *     their own, so there is nothing to set — but a change here that reached
 *     for `no-cors` or a form post would break it silently.
 *   · **The import routes.** `PUT /import/:id/files/…` and
 *     `POST /import/:id/ready` are Compose's, authorised by an upload ticket
 *     rather than a session. They are deliberately absent below: nothing in
 *     the browser has a ticket, and a client method for them would be an
 *     invitation to try.
 */

import type {
  AssignRequestWire,
  AssignResultWire,
  AssignmentsWire,
  BundleCardWire,
  BundleCoursesWire,
  BundleCreateWire,
  BundlePatchWire,
  BundleReportWire,
  BundleWire,
  BundlesWire,
  CatalogueWire,
  CertificatesWire,
  ExternalInviteResultWire,
  ExternalInviteWire,
  ExternalLearnersWire,
  ExternalLinkWire,
  CourseCardWire,
  CoursePatchWire,
  CourseWire,
  FeedbackFormWire,
  FeedbackResponsesWire,
  FeedbackSubmitWire,
  FeedbackViewWire,
  HealthWire,
  HomeWire,
  LearningWire,
  MeWire,
  NotificationsWire,
  NudgeRequestWire,
  NudgeResultWire,
  OkWire,
  PeopleWire,
  PlayWire,
  ProgressCommitWire,
  ProgressResultWire,
  RemindersPatchWire,
  RemindersWire,
  ReportWire,
  SearchWire,
  TeachBundlesWire,
  TeachCoursesWire,
  TeachersWire,
  AssignScope
} from '@wire';

/** Every route lives under this prefix; nothing else on this origin is API. */
const B = '/api/lms';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
    readonly requestId?: string
  ) {
    super(message);
  }

  /**
   * True for an unhandled exception in the Worker, as opposed to a refusal it
   * meant to make.
   *
   * Keyed on the *code*, not the status, for the reason website-status
   * records: keying on the status swept in every deliberate 503 —
   * `not_configured` and `migrations_pending` both — and offered a support
   * reference for a problem the server had just diagnosed in full.
   */
  get isInternal(): boolean {
    return this.code === 'internal' || (this.status >= 500 && this.code === 'unknown');
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: { 'content-type': 'application/json', accept: 'application/json', ...(init.headers ?? {}) }
    });
  } catch {
    // A genuinely failed fetch — no response at all. Distinct from a 5xx,
    // which the server chose to send and can explain.
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection.');
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    const envelope = payload as { error?: Record<string, unknown>; requestId?: string } | null;
    const err = envelope?.error;
    throw new ApiError(
      res.status,
      (err?.code as string) ?? 'unknown',
      (err?.message as string) ?? 'Something went wrong.',
      err ?? {},
      typeof envelope?.requestId === 'string' ? envelope.requestId : undefined
    );
  }

  return payload as T;
}

/** A body is always sent, even an empty object: the Worker's `readJson`
 *  refuses a request that declares JSON and carries nothing. */
const post = <T>(path: string, body: unknown = {}) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

const enc = encodeURIComponent;

/** Query string from whatever is actually set. Empty and undefined are the
 *  same thing — "no filter" — so neither reaches the server. */
const q = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

/**
 * The sentence to put in front of somebody, given a thrown error.
 *
 * Used by every page's catch block. An internal failure gets the request id
 * appended so support has something to search on; a deliberate refusal gets
 * the server's own words, because the server knows why.
 */
export function errorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  if (!err.isInternal) return err.message;
  return err.requestId
    ? `${err.message} If it keeps happening, quote reference ${err.requestId}.`
    : err.message;
}

// ── who you are, and what this deployment can do ────────────────────────────

export const api = {
  /** The first request every screen waits on. 200 with `organisation: null`
   *  for an account outside every organisation — not an error. */
  me: () => request<MeWire>(`${B}/me`),
  /** 503 when the deployment is unmigrated. `state.tsx` reads it to tell
   *  "not finished setting up" from "not answering". */
  health: () => request<HealthWire>(`${B}/health`),
  /** Ends an external learner's session. */
  externalSignOut: () => post<OkWire>(`${B}/external/signout`)
};

// ── learning ────────────────────────────────────────────────────────────────

export const learn = {
  /** Screen 1a, whole: counts, assigned work, bundles, new in the company. */
  home: () => request<HomeWire>(`${B}/home`),
  learning: () => request<LearningWire>(`${B}/learning`),
  catalogue: () => request<CatalogueWire>(`${B}/catalogue`),
  /** The topbar's "courses, bundles, people". Server-side — titles are
   *  encrypted, so there is nothing here to filter. */
  search: (query: string) => request<SearchWire>(`${B}/search${q({ q: query })}`),
  course: (id: string) => request<CourseWire>(`${B}/courses/${enc(id)}`),
  /**
   * Open a course. Returns the player URL with a signed ticket in its path.
   *
   * Put it in the iframe's `src` and nowhere else — not a link, not a title,
   * not `history.pushState`. It is the whole authorisation for twelve hours.
   */
  play: (id: string) => post<PlayWire>(`${B}/courses/${enc(id)}/play`),
  /** One commit from the bridge, forwarded unchanged. The frame's message and
   *  this body are the same object — see `player/protocol.ts`. */
  progress: (id: string, commit: ProgressCommitWire) =>
    post<ProgressResultWire>(`${B}/courses/${enc(id)}/progress`, commit),
  bundles: () => request<BundlesWire>(`${B}/bundles`),
  bundle: (id: string) => request<BundleWire>(`${B}/bundles/${enc(id)}`),
  /** The done screen: the form, and whether it has already been answered. */
  feedback: (id: string) => request<FeedbackViewWire>(`${B}/courses/${enc(id)}/feedback`),
  sendFeedback: (id: string, body: FeedbackSubmitWire) =>
    post<OkWire>(`${B}/courses/${enc(id)}/feedback`, body),
  /** Skip is recorded. "Asked and declined" and "never asked" are different
   *  states and the screen has to tell them apart. */
  feedbackSkip: (id: string) => post<OkWire>(`${B}/courses/${enc(id)}/feedback/skip`),
  /** The caller's own certificates. */
  certificates: () => request<CertificatesWire>(`${B}/certificates`),
  /** A plain link, so the browser downloads it with the session cookie. */
  certificatePdfUrl: (id: string) => `${B}/certificates/${enc(id)}/pdf`
};

// ── teaching ────────────────────────────────────────────────────────────────

/** Narrowing shared by the report and its CSV, so the two cannot disagree
 *  about what is on screen. */
export interface ReportFilter {
  /** An enterprise role id — the design's team filter. */
  team?: string;
  from?: number;
  to?: number;
}

export const teach = {
  /** Screen 1d: the four stats and every course this account may manage. */
  courses: () => request<TeachCoursesWire>(`${B}/teach/courses`),
  bundles: () => request<TeachBundlesWire>(`${B}/teach/bundles`),
  /** Ids, names, handles and teams. No emails — those appear in a course
   *  report and nowhere else. */
  people: () => request<PeopleWire>(`${B}/people`),

  // Courses. There is no `create` here: a course is created by Compose's
  // upload, through the bearer door, and the dashboard's **Upload from
  // Compose** button opens a dialog explaining that rather than a form.
  updateCourse: (id: string, body: CoursePatchWire) =>
    patch<CourseCardWire>(`${B}/courses/${enc(id)}`, body),
  deleteCourse: (id: string) => del<OkWire>(`${B}/courses/${enc(id)}`),
  publish: (id: string) => post<CourseCardWire>(`${B}/courses/${enc(id)}/publish`),
  /** Keeps every enrolment. Somebody halfway through keeps their place. */
  unpublish: (id: string) => post<CourseCardWire>(`${B}/courses/${enc(id)}/unpublish`),

  feedbackForm: {
    get: (id: string) => request<FeedbackFormWire>(`${B}/courses/${enc(id)}/feedback-form`),
    /** PUT: one call states the whole form, because reordering questions is
     *  not expressible as a patch of any one of them. */
    save: (id: string, form: FeedbackFormWire) =>
      put<FeedbackFormWire>(`${B}/courses/${enc(id)}/feedback-form`, form),
    responses: (id: string) =>
      request<FeedbackResponsesWire>(`${B}/courses/${enc(id)}/feedback/responses`)
  },

  /** Screen 1e, whole — every panel from one request. */
  report: (id: string, filter: ReportFilter = {}) =>
    request<ReportWire>(`${B}/courses/${enc(id)}/report${q({ ...filter })}`),
  /**
   * The same rows as a file, for an `<a href download>`.
   *
   * A URL rather than a fetch: the browser's own download beats reading a
   * multi-megabyte CSV into memory to hand it back as a blob, and the cookie
   * goes with it either way.
   */
  reportCsvUrl: (id: string, filter: ReportFilter = {}) =>
    `${B}/courses/${enc(id)}/report.csv${q({ ...filter })}`,
  /** `userIds: null` nudges everyone past the deadline and unfinished. */
  nudge: (id: string, body: NudgeRequestWire) =>
    post<NudgeResultWire>(`${B}/courses/${enc(id)}/nudge`, body),

  assignments: {
    /** Filter by one target, or neither to list the organisation's. */
    list: (opts: { courseId?: string; bundleId?: string } = {}) =>
      request<AssignmentsWire>(`${B}/assignments${q({ ...opts })}`),
    /** Screen 1f, submitted. One call states the whole dialog. */
    create: (body: AssignRequestWire) => post<AssignResultWire>(`${B}/assignments`, body),
    revoke: (id: string) => del<OkWire>(`${B}/assignments/${enc(id)}`)
  },

  createBundle: (body: BundleCreateWire) => post<BundleCardWire>(`${B}/bundles`, body),
  updateBundle: (id: string, body: BundlePatchWire) =>
    patch<BundleCardWire>(`${B}/bundles/${enc(id)}`, body),
  deleteBundle: (id: string) => del<OkWire>(`${B}/bundles/${enc(id)}`),
  /** The whole contents, in order. */
  setBundleCourses: (id: string, body: BundleCoursesWire) =>
    put<OkWire>(`${B}/bundles/${enc(id)}/courses`, body),
  bundleReport: (id: string) => request<BundleReportWire>(`${B}/bundles/${enc(id)}/report`),
  courseCertificates: (id: string) =>
    request<CertificatesWire>(`${B}/courses/${enc(id)}/certificates`),

  externals: {
    list: () => request<ExternalLearnersWire>(`${B}/externals`),
    invite: (body: ExternalInviteWire) => post<ExternalInviteResultWire>(`${B}/externals`, body),
    link: (id: string) => post<ExternalLinkWire>(`${B}/externals/${enc(id)}/link`),
    revoke: (id: string) => del<OkWire>(`${B}/externals/${enc(id)}`)
  }
};

// ── the organisation ────────────────────────────────────────────────────────

export const org = {
  teachers: () => request<TeachersWire>(`${B}/org/teachers`),
  /** The whole list. Removing a grant is sending it without that scope. */
  setTeachers: (scopes: AssignScope[]) => put<TeachersWire>(`${B}/org/teachers`, { scopes }),
  /** People.tsx reads the current schedule off `teach.people()`'s own
   *  `reminders` field — there is no separate screen that only needs this. */
  setReminders: (body: RemindersPatchWire) => patch<RemindersWire>(`${B}/org/reminders`, body)
};

// ── the bell ────────────────────────────────────────────────────────────────

export const notifications = {
  list: () => request<NotificationsWire>(`${B}/notifications`),
  /** One id marks one; none marks everything. Read state is per product —
   *  this does not mark Cloud's mirror of the same row. */
  read: (id?: string) => post<OkWire>(`${B}/notifications/read`, { id })
};

/** Re-exported for any module that grows its own section of this client, so
 *  each keeps one place to add to without re-declaring the plumbing. */
export { request, post, patch, put, del, B };
